import React from "react";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import DesktopWindowsOutlinedIcon from "@mui/icons-material/DesktopWindowsOutlined";
import {
  useThemeStore,
  ThemePreference,
} from "components/main/stores/themeStore";
import { IconPicker } from "components/common/IconPicker";
import { prefersReducedMotion } from "@/lib/motion";

const OPTIONS: {
  value: ThemePreference;
  label: string;
  Icon: typeof LightModeOutlinedIcon;
}[] = [
  { value: "light", label: "Light", Icon: LightModeOutlinedIcon },
  { value: "dark", label: "Dark", Icon: DarkModeOutlinedIcon },
  { value: "system", label: "System", Icon: DesktopWindowsOutlinedIcon },
];

const systemTheme = (): string =>
  window.matchMedia?.("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";

/// The page cross-fades into the new theme while the picker itself, captured
/// on its own (`view-transition-name`), switches at once and slides its thumb.
const switchTheme = (apply: () => void) => {
  if (typeof document.startViewTransition !== "function") return apply();
  if (prefersReducedMotion()) return apply();
  // WebKit's snapshot drops `backdrop-filter`, so the pane behind the settings card unblurs mid-fade.
  if (navigator.vendor === "Apple Computer, Inc.") return apply();
  document.startViewTransition(apply);
};

export const ThemePicker: React.FC = () => {
  const { preference, setPreference } = useThemeStore();
  return (
    <IconPicker
      ariaLabel="Theme"
      className="theme-picker"
      value={preference}
      onChange={(next) => switchTheme(() => setPreference(next))}
      options={OPTIONS.map((o) =>
        o.value === "system"
          ? { ...o, tooltip: `${o.label} · ${systemTheme()} now` }
          : o,
      )}
    />
  );
};
